import { Head, router } from '@inertiajs/react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import React, { useState } from 'react'
import AppLayout from '../../components/layouts/AppLayout'
import type { EntryNotificationPreference } from '../../types'

interface Props {
  globalEmailEnabled: boolean
  entryPreferences: EntryNotificationPreference[]
}

export default function NotificationPreferences({ globalEmailEnabled, entryPreferences }: Props) {
  const [globalEnabled, setGlobalEnabled] = useState(globalEmailEnabled)
  const [entryPrefs, setEntryPrefs] = useState(entryPreferences)
  const [saving, setSaving] = useState(false)

  const handleEntryToggle = (appEntryId: number) => {
    setEntryPrefs(prev => prev.map(ep =>
      ep.appEntryId === appEntryId ? { ...ep, emailEnabled: !ep.emailEnabled } : ep
    ))
  }

  const handleSave = () => {
    setSaving(true)
    router.patch('/dashboard/notification_preferences', {
      global_email_enabled: globalEnabled,
      entry_preferences: entryPrefs.map(ep => ({
        app_entry_id: ep.appEntryId,
        email_enabled: ep.emailEnabled,
      })),
    }, {
      onFinish: () => setSaving(false),
    })
  }

  return (
    <AppLayout title="Notification Preferences">
      <Head title="Notification Preferences" />
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => router.visit('/dashboard')}
          sx={{ mb: 2 }}
        >
          Back to Dashboard
        </Button>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Global Settings</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={globalEnabled}
                  onChange={() => setGlobalEnabled(!globalEnabled)}
                />
              }
              label="Email me when followed apps publish new versions"
            />
          </CardContent>
        </Card>

        {entryPrefs.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Per-App Settings</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Override the global setting for individual apps.
              </Typography>
              {entryPrefs.map((ep, index) => (
                <React.Fragment key={ep.appEntryId}>
                  {index > 0 && <Divider />}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={ep.emailEnabled}
                        onChange={() => handleEntryToggle(ep.appEntryId)}
                      />
                    }
                    label={ep.appEntryName}
                    sx={{ py: 0.5, width: '100%' }}
                  />
                </React.Fragment>
              ))}
            </CardContent>
          </Card>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Preferences'}
          </Button>
        </Box>
      </Container>
    </AppLayout>
  )
}
